import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Input,
  Label,
  PageContainer,
  PageIntroCard,
  PageSectionCard,
  Skeleton,
  toast,
} from '@kmgestao/ui';

import {
  useCreateWidget,
  useDashboard,
  useDashboardWidgets,
  useDeleteWidget,
  useUpdateDashboard,
  useUpdateWidget,
  type DashboardWidget,
  type WidgetType,
} from '../../../features/dashboards/index.js';
import { SuperadminShell } from '../SuperadminShell.js';

import { WidgetPreview } from './WidgetPreview.js';

const WIDGET_TYPES: { value: WidgetType; label: string; hint: string }[] = [
  { value: 'kpi', label: 'KPI tile', hint: 'Número grande + tendência.' },
  { value: 'line', label: 'Linha', hint: 'Série temporal.' },
  { value: 'bar', label: 'Barra', hint: 'Comparação entre categorias.' },
  { value: 'area', label: 'Área', hint: 'Volume acumulado ao longo do tempo.' },
  { value: 'pie', label: 'Pizza', hint: 'Distribuição percentual.' },
  { value: 'table', label: 'Tabela', hint: 'Lista detalhada (DataTable).' },
  { value: 'narrative', label: 'Texto markdown', hint: 'Blocos narrativos com placeholders.' },
  { value: 'ai_card', label: 'Cartão IA', hint: 'Insight gerado pelo AI Gateway.' },
];

/**
 * Editor de um dashboard. MVP:
 *  - Form inline para renomear / mudar descrição
 *  - Lista de widgets com CRUD (criar, editar título, apagar)
 *  - WidgetPreview renderiza placeholder com tipo + query_ref
 *
 * Fora do escopo M0 (entram em M1):
 *  - Drag-and-drop para reposicionar (posição é ajustada via form)
 *  - Execução real da query (hoje é só metadata)
 *  - Preview em tempo real (hoje é estático)
 */
export function DashboardEditorPage() {
  const { id } = useParams<{ id: string }>();
  const dashboard = useDashboard(id);
  const widgets = useDashboardWidgets(id);
  const updateDashboard = useUpdateDashboard();
  const createWidget = useCreateWidget();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dirty, setDirty] = useState(false);

  const isEditingLoaded = dashboard.data && !dirty;
  const displayedName = dirty ? name : (dashboard.data?.name ?? '');
  const displayedDescription = dirty ? description : (dashboard.data?.description ?? '');

  const onBeginEdit = () => {
    if (!dashboard.data) return;
    setName(dashboard.data.name);
    setDescription(dashboard.data.description ?? '');
    setDirty(true);
  };

  const onCancelEdit = () => {
    setDirty(false);
  };

  const onSaveEdit = async () => {
    if (!dashboard.data) return;
    await updateDashboard.mutateAsync(
      {
        id: dashboard.data.id,
        name: name.trim(),
        description: description.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success('Dashboard atualizado.');
          setDirty(false);
        },
        onError: (err) => toast.error('Falha ao salvar', { description: (err as Error).message }),
      },
    );
  };

  return (
    <SuperadminShell>
      <PageContainer>
        <PageIntroCard
          eyebrow={
            <Link to="/superadmin/dashboards" className="hover:underline">
              ← Todos os dashboards
            </Link>
          }
          title={dashboard.isLoading ? 'Carregando dashboard…' : displayedName || 'Dashboard'}
          description={
            dashboard.isLoading
              ? undefined
              : displayedDescription || 'Sem descrição — adicione uma para dar contexto.'
          }
          meta={
            dashboard.data ? (
              <div className="flex gap-2">
                <Badge variant="secondary">{dashboard.data.scope}</Badge>
                {dashboard.data.is_shared ? <Badge>Compartilhado</Badge> : null}
              </div>
            ) : null
          }
          actions={
            isEditingLoaded ? (
              <Button variant="outline" size="sm" onClick={onBeginEdit}>
                Editar metadados
              </Button>
            ) : dirty ? (
              <div className="flex gap-2">
                <Button size="sm" onClick={onSaveEdit} disabled={updateDashboard.isPending}>
                  {updateDashboard.isPending ? 'Salvando…' : 'Salvar'}
                </Button>
                <Button variant="outline" size="sm" onClick={onCancelEdit}>
                  Cancelar
                </Button>
              </div>
            ) : null
          }
        >
          {dirty ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="edit-name">Nome</Label>
                <Input
                  id="edit-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-desc">Descrição</Label>
                <Input
                  id="edit-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
          ) : null}
        </PageIntroCard>

        <NewWidgetForm
          dashboardId={id ?? ''}
          onSuccess={() => toast.success('Widget adicionado.')}
          disabled={!id || createWidget.isPending}
        />

        <PageSectionCard
          title="Widgets"
          description={
            widgets.data?.length
              ? `${widgets.data.length} widget${widgets.data.length === 1 ? '' : 's'} configurado${widgets.data.length === 1 ? '' : 's'}.`
              : 'Nenhum widget — use o formulário acima para adicionar.'
          }
        >
          {widgets.isLoading ? (
            <div className="grid gap-4 md:grid-cols-2">
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : widgets.data?.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Adicione KPIs, gráficos Recharts, tabelas ou cartões IA usando o formulário acima.
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {widgets.data?.map((w) => (
                <WidgetCard key={w.id} widget={w} />
              ))}
            </div>
          )}
        </PageSectionCard>
      </PageContainer>
    </SuperadminShell>
  );
}

interface NewWidgetFormProps {
  dashboardId: string;
  onSuccess: () => void;
  disabled?: boolean;
}

function NewWidgetForm({ dashboardId, onSuccess, disabled }: NewWidgetFormProps) {
  const create = useCreateWidget();
  const [widgetType, setWidgetType] = useState<WidgetType>('kpi');
  const [title, setTitle] = useState('');
  const [queryRef, setQueryRef] = useState('');

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!dashboardId || !title.trim() || !queryRef.trim()) return;
    await create.mutateAsync(
      {
        dashboard_id: dashboardId,
        widget_type: widgetType,
        title: title.trim(),
        subtitle: null,
        query_ref: queryRef.trim(),
        config: {},
        position: { x: 0, y: 0, w: 4, h: 2 },
      },
      {
        onSuccess: () => {
          onSuccess();
          setTitle('');
          setQueryRef('');
        },
        onError: (err) =>
          toast.error('Falha ao adicionar widget', { description: (err as Error).message }),
      },
    );
  };

  return (
    <PageSectionCard
      title="Adicionar widget"
      description={
        `Informe o tipo, um título e a referência da fonte de dados (ex: ` +
        `"analytics.mv_global_kpis"). A execução real da query entra em M1 ` +
        `— hoje o preview é estático.`
      }
    >
      <form className="grid gap-3 sm:grid-cols-[auto,1fr,1fr,auto]" onSubmit={onSubmit} noValidate>
        <div className="space-y-1.5">
          <Label htmlFor="widget-type">Tipo</Label>
          <select
            id="widget-type"
            value={widgetType}
            onChange={(e) => setWidgetType(e.target.value as WidgetType)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            {WIDGET_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="widget-title">Título</Label>
          <Input
            id="widget-title"
            placeholder="Ex: Receita MRR"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={120}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="widget-query">Query ref</Label>
          <Input
            id="widget-query"
            placeholder="analytics.mv_global_kpis"
            value={queryRef}
            onChange={(e) => setQueryRef(e.target.value)}
            required
            maxLength={120}
          />
        </div>
        <div className="flex items-end">
          <Button type="submit" disabled={disabled}>
            {create.isPending ? 'Adicionando…' : 'Adicionar'}
          </Button>
        </div>
      </form>
    </PageSectionCard>
  );
}

function WidgetCard({ widget }: { widget: DashboardWidget }) {
  const update = useUpdateWidget();
  const remove = useDeleteWidget();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(widget.title);
  const [subtitle, setSubtitle] = useState(widget.subtitle ?? '');

  const onSave = async () => {
    await update.mutateAsync(
      {
        id: widget.id,
        dashboard_id: widget.dashboard_id,
        title: title.trim(),
        subtitle: subtitle.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success('Widget atualizado.');
          setIsEditing(false);
        },
        onError: (err) => toast.error('Falha ao salvar', { description: (err as Error).message }),
      },
    );
  };

  const onDelete = async () => {
    if (!window.confirm(`Apagar o widget "${widget.title}"? Essa ação é irreversível.`)) return;
    await remove.mutateAsync(
      { id: widget.id, dashboard_id: widget.dashboard_id },
      {
        onSuccess: () => toast.success('Widget apagado.'),
        onError: (err) => toast.error('Falha ao apagar', { description: (err as Error).message }),
      },
    );
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      {isEditing ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`w-title-${widget.id}`}>Título</Label>
            <Input
              id={`w-title-${widget.id}`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`w-sub-${widget.id}`}>Subtítulo</Label>
            <Input
              id={`w-sub-${widget.id}`}
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Opcional"
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={onSave} disabled={update.isPending}>
              {update.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold">{widget.title}</h3>
              {widget.subtitle ? (
                <p className="text-sm text-muted-foreground">{widget.subtitle}</p>
              ) : null}
            </div>
            <Badge variant="secondary">{widget.widget_type}</Badge>
          </div>
          <WidgetPreview widget={widget} />
          <div className="flex flex-wrap gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
              Editar
            </Button>
            <Button size="sm" variant="outline" onClick={onDelete} disabled={remove.isPending}>
              Apagar
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">query_ref:</span> {widget.query_ref}
          </p>
        </div>
      )}
    </div>
  );
}
